import { useEffect } from 'react';
import { Button, Card, CardHeader, CardTitle, CardContent, cn } from '@exam-prep/ui';
import { useLoad } from '../../lib/useLoad';
import { untilIst } from '../../lib/format';
import { api } from '../../lib/api';
import { ErrorBox, Loading } from '../../components/Feedback';
import type { Props } from './model/grand-mock-results';
import type { GmResults } from './model/types';
import { Panel } from './ui/GrandMockParts';
import { RESULTS, GM } from './model/copy';
import { formatDuration } from '@exam-prep/ui';
import { prizesVisible, accuracyPct, inr } from './model/flow';

/** Results after the announced time: your score and rank, the top of the leaderboard. */
export function GrandMockResultsScreen({ gmId, onBack, onViewAnswers }: Props) {
  const { data, error, reload } = useLoad(
    () => api<GmResults>('GET', `/app/grand-mock/${gmId}/results`),
    [gmId],
  );
  const pending = !!data && !data.released;
  // While results are pending, look again every minute so they appear on their own.
  useEffect(() => {
    if (!pending) return;
    const id = setInterval(() => {
      if (!document.hidden) reload();
    }, 60_000);
    return () => clearInterval(id);
  }, [pending, reload]);

  if (error && !data) return <ErrorBox error={error} retry={reload} />;
  if (!data) return <Loading />;

  if (!data.released)
    return (
      <div className="space-y-4 p-4">
        <Panel title={RESULTS.title}>
          <p role="status" className="font-display text-base font-semibold">
            {GM.resultsPending}
          </p>
          <p>{GM.resultsPendingBody}</p>
          <p className="text-muted-foreground">Expected: {untilIst(data.resultsAt)}</p>
          <Button variant="outline" className="w-full" onClick={reload}>
            Check again
          </Button>
        </Panel>
        <Button variant="outline" size="lg" className="w-full" onClick={onBack}>
          {GM.back}
        </Button>
      </div>
    );

  const { me, leaderboard, cohort, grandMock: gm } = data;
  const prizes = prizesVisible(gm) ? (gm.prizesInr ?? []) : [];
  const ranked = !!me && !me.disqualified && me.rank !== null;
  const inTop = leaderboard.some((r) => r.me);
  return (
    <div className="space-y-4 p-4 pb-8">
      <Card className="live-card">
        <CardHeader className="items-center text-center">
          <p className="text-xs font-semibold tracking-[0.14em] text-muted-foreground uppercase">
            {RESULTS.title}
          </p>
          <CardTitle>{gm.title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-center text-sm">
          {me && ranked ? (
            <>
              <p className="font-display text-3xl font-bold">
                <span className="brand-accent-text">{RESULTS.rankOf(me.rank ?? 0, cohort)}</span>
              </p>
              <div className="grid grid-cols-3 gap-2">
                <span className="stat-tile">
                  <b className="font-display text-xl tabular-nums">
                    {me.score}/{me.maxScore}
                  </b>
                  <span className="text-xs text-muted-foreground">{RESULTS.score}</span>
                </span>
                <span className="stat-tile">
                  <b className="font-display text-xl tabular-nums">{accuracyPct(me.accuracy)}</b>
                  <span className="text-xs text-muted-foreground">{RESULTS.accuracy}</span>
                </span>
                <span className="stat-tile">
                  <b className="font-display text-xl tabular-nums">{formatDuration(me.timeMs)}</b>
                  <span className="text-xs text-muted-foreground">{RESULTS.time}</span>
                </span>
              </div>
              {me.attemptId ? (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => onViewAnswers(String(me.attemptId))}
                >
                  {RESULTS.viewAnswers}
                </Button>
              ) : null}
            </>
          ) : me?.disqualified ? (
            <p role="status">{RESULTS.disqualified}</p>
          ) : (
            <p role="status">{RESULTS.notTaken}</p>
          )}
        </CardContent>
      </Card>

      <Panel title={RESULTS.leaderboard}>
        {leaderboard.length ? (
          <ol className="space-y-1">
            {leaderboard.map((r) => (
              <li
                key={`${String(r.rank)}-${r.name}`}
                className={cn(
                  'flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 tabular-nums',
                  r.me && 'border border-brand-amber bg-accent font-semibold',
                )}
              >
                <span className="min-w-0 truncate">
                  #{r.rank} {r.name}
                  {r.me ? ' (you)' : ''}
                </span>
                <span className="shrink-0 text-right">
                  {r.score}/{r.maxScore} · {accuracyPct(r.accuracy)} · {formatDuration(r.timeMs)}
                  {r.rank && prizes[r.rank - 1] ? ` · ${inr(prizes[r.rank - 1] ?? 0)}` : ''}
                </span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-muted-foreground">{RESULTS.empty}</p>
        )}
        {ranked && !inTop ? (
          <p className="rounded-lg border border-brand-amber bg-accent px-2 py-1.5 font-semibold tabular-nums">
            {RESULTS.yourPosition}: #{me.rank} · {me.score}/{me.maxScore} ·{' '}
            {accuracyPct(me.accuracy)} · {formatDuration(me.timeMs)}
          </p>
        ) : null}
      </Panel>

      <Button variant="outline" size="lg" className="w-full" onClick={onBack}>
        {GM.back}
      </Button>
    </div>
  );
}
